import * as Yup from "yup";
import { useEffect, useMemo, useState } from "react";
import {
  IProduct,
  ProductType,
  ProductCategory,
} from "../../../@types/product";
import { Brands } from "../../../@types/brand.type";
import { Suppliers } from "../../../@types/supplier.type";
// form
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// @mui
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Stack,
  Typography,
  DialogActions,
  Button,
  MenuItem,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  RHFTextField,
  RHFSelect,
  RHFAutocomplete,
} from "../../../components/hook-form";
import FormProvider from "../../../components/hook-form/FormProvider";
import { ProductService } from "../../../api/product.service";
import { BrandService } from "../../../api/brand.service";
import { SupplierService } from "../../../api/supplier.service";

type ProductProps = {
  isEdit?: boolean;
  isView?: boolean;
  selectedProduct?: IProduct;
  onCancel: (value?: boolean) => void;
};

type FormValuesProps = {
  name: string;
  sku: string;
  code: string;
  category: string;
  type: string;
  purchasePrice: number;
  salePrice: number;
  discountPercentage: number | "";
  vatRate: number | "";
  brandId: number | "";
  supplierId: number | "";
  description?: string;
  productDetails?: {
    model?: string;
    color?: string;
    material?: string;
    size?: string;
    shape?: string;
    modality?: string;
  };
};

const CATEGORY_BY_TYPE = {
  [ProductType.FRAMES]: [
    {
      label: "Prescription Frames",
      value: ProductCategory.PRESCRIPTION_FRAMES,
    },
    { label: "Reading Glasses", value: ProductCategory.READING_GLASSES },
    { label: "Safety Glasses", value: ProductCategory.SAFETY_GLASSES },
  ],
  [ProductType.LENSES]: [
    { label: "Single Vision", value: ProductCategory.SINGLE_VISION },
    { label: "Bifocal", value: ProductCategory.BIFOCAL },
    { label: "Progressive", value: ProductCategory.PROGRESSIVE },
  ],
  [ProductType.CONTACT_LENSES]: [
    { label: "Daily Disposable", value: ProductCategory.DAILY_DISPOSABLE },
    { label: "Weekly Disposable", value: ProductCategory.WEEKLY_DISPOSABLE },
    { label: "Monthly Disposable", value: ProductCategory.MONTHLY_DISPOSABLE },
    { label: "Colored Contacts", value: ProductCategory.COLORED_CONTACTS },
  ],
  [ProductType.SUNGLASSES]: [
    {
      label: "Prescription Sunglasses",
      value: ProductCategory.PRESCRIPTION_SUNGLASSES,
    },
    { label: "Polarized Sunglasses", value: ProductCategory.POLARIZED_SUNGLASSES },
    {
      label: "Photochromic Sunglasses",
      value: ProductCategory.PHOTOCHROMIC_SUNGLASSES,
    },
  ],
  [ProductType.ACCESSORIES]: [
    { label: "Cases", value: ProductCategory.CASES },
    { label: "Cleaning Solutions", value: ProductCategory.CLEANING_SOLUTIONS },
    { label: "Straps", value: ProductCategory.STRAPS },
    { label: "Repair Kits", value: ProductCategory.REPAIR_KITS },
  ],
  [ProductType.SUNDRIES]: [
    { label: "Consumables", value: ProductCategory.CONSUMABLES },
    { label: "Cloths & Wipes", value: ProductCategory.CLOTHS_AND_WIPES },
    { label: "Batteries", value: ProductCategory.BATTERIES },
  ],
  [ProductType.SOLUTION]: [
    { label: "Saline Solution", value: ProductCategory.SALINE_SOLUTION },
    {
      label: "Multi-purpose Solution",
      value: ProductCategory.MULTIPURPOSE_SOLUTION,
    },
    {
      label: "Hydrogen Peroxide Solution",
      value: ProductCategory.HYDROGEN_PEROXIDE_SOLUTION,
    },
  ],
};

const TYPE_OPTIONS = [
  { label: "Frame", value: ProductType.FRAMES },
  { label: "Lens", value: ProductType.LENSES },
  { label: "CL", value: ProductType.CONTACT_LENSES },
  { label: "Sunglasses", value: ProductType.SUNGLASSES },
  { label: "Accessory", value: ProductType.ACCESSORIES },
  { label: "Sundries", value: ProductType.SUNDRIES },
  { label: "Solution", value: ProductType.SOLUTION },
];

const COMMON_COLOR_OPTIONS = [
  "Black",
  "White",
  "Silver",
  "Gold",
  "Brown",
  "Blue",
  "Green",
  "Red",
  "Pink",
  "Purple",
  "Grey",
  "Transparent",
];

const MATERIAL_OPTIONS_BY_TYPE = {
  [ProductType.FRAMES]: [
    "Acetate",
    "Metal",
    "Titanium",
    "Stainless Steel",
    "TR90",
    "Nylon",
  ],
  [ProductType.LENSES]: [
    "CR-39",
    "Polycarbonate",
    "Trivex",
    "High Index",
    "Glass",
  ],
  [ProductType.CONTACT_LENSES]: [
    "Hydrogel",
    "Silicone Hydrogel",
    "RGP",
    "Hybrid",
    "Scleral",
  ],
  [ProductType.SUNGLASSES]: [
    "Acetate",
    "Metal",
    "Polycarbonate",
    "Nylon",
    "TR90",
  ],
  [ProductType.ACCESSORIES]: [
    "Plastic",
    "Silicone",
    "Microfiber",
    "Nylon",
    "Leather",
  ],
  [ProductType.SUNDRIES]: [
    "Plastic",
    "Microfiber",
    "Paper",
    "Rubber",
  ],
  [ProductType.SOLUTION]: [
    "Saline",
    "Hydrogen Peroxide",
    "Multipurpose",
    "Preservative-Free",
  ],
};

const ALL_MATERIAL_OPTIONS = Array.from(
  new Set(Object.values(MATERIAL_OPTIONS_BY_TYPE).flat())
);

const VAT_RATE_OPTIONS = [0, 5, 20];

const MODALITY_OPTIONS = [
  "Daily",
  "Weekly",
  "Bi-weekly",
  "Monthly",
  "Quarterly",
  "Half-yearly",
  "Yearly",
];

export default function ProductNewEditForm({
  isEdit,
  isView,
  selectedProduct,
  onCancel,
}: ProductProps) {
  const { enqueueSnackbar } = useSnackbar();

  const [colorOptions, setColorOptions] = useState<string[]>(
    COMMON_COLOR_OPTIONS
  );

  const [brandOptions, setBrandOptions] = useState<Brands[]>([]);
  const [supplierOptions, setSupplierOptions] = useState<Suppliers[]>([]);

  const NewProductSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required("Product name is required")
      .min(2, "Product name must be at least 2 characters")
      .max(100, "Product name must be 100 characters or less"),
    code: Yup.string()
      .trim()
      .max(100, "Product code must be 100 characters or less")
      .optional(),
    sku: Yup.string()
      .trim()
      .max(100, "SKU must be 100 characters or less")
      .optional(),
    vatRate: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value,
      )
      .typeError("VAT rate must be a number")
      .optional()
      .oneOf(VAT_RATE_OPTIONS as any, "VAT rate must be one of: 0%, 5%, 20%"),
    brandId: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value,
      )
      .typeError("Invalid brand selection")
      .integer("Invalid brand selection")
      .positive("Invalid brand selection")
      .optional(),
    supplierId: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value,
      )
      .typeError("Invalid supplier selection")
      .integer("Invalid supplier selection")
      .positive("Invalid supplier selection")
      .optional(),
    purchasePrice: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value,
      )
      .typeError("Purchase price must be a number")
      .required("Purchase price is required")
      .min(0, "Purchase price must be greater than or equal to 0"),

    salePrice: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value,
      )
      .typeError("Sale price must be a number")
      .required("Sale price is required")
      .min(0, "Sale price must be greater than or equal to 0")
      .test(
        "sale-vs-purchase",
        "Sale price must be greater than or equal to purchase price",
        function (salePrice) {
          const purchasePrice = this.parent.purchasePrice;
          if (salePrice === undefined || purchasePrice === undefined)
            return true;
          return Number(salePrice) >= Number(purchasePrice);
        },
      ),
    discountPercentage: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value,
      )
      .typeError("Discount must be a number")
      .min(0, "Discount must be greater than or equal to 0")
      .max(100, "Discount cannot be more than 100")
      .optional(),
    category: Yup.string()
      .required("Category is required")
      .oneOf(Object.values(ProductCategory), "Please select a valid category"),
    type: Yup.string()
      .required("Type is required")
      .oneOf(Object.values(ProductType), "Please select a valid type"),
    description: Yup.string()
      .trim()
      .max(500, "Description must be 500 characters or less")
      .optional(),
    productDetails: Yup.object().shape({
      model: Yup.string()
        .trim()
        .required("Model is required")
        .max(100, "Model must be 100 characters or less")
        .matches(
          /^[a-zA-Z0-9\s\-_/().]+$/,
          "Model contains invalid characters",
        ),
      color: Yup.string().trim().required("Color is required"),
      material: Yup.string()
        .trim()
        .oneOf(ALL_MATERIAL_OPTIONS, "Please select a valid material")
        .optional(),
      size: Yup.string()
        .trim()
        .required("Size is required")
        .max(50, "Size must be 50 characters or less")
        .matches(/^[a-zA-Z0-9\s\-_/().]+$/, "Size contains invalid characters"),
      shape: Yup.string()
        .trim()
        .required("Shape is required")
        .max(100, "Shape must be 100 characters or less")
        .matches(
          /^[a-zA-Z0-9\s\-_/().]+$/,
          "Shape contains invalid characters",
        ),
      modality: Yup.string()
        .trim()
        .oneOf(MODALITY_OPTIONS, "Please select a valid modality")
        .optional(),
    }),
  });

  const defaultValues = useMemo(
    () => ({
      name: selectedProduct?.name || "",
      code: selectedProduct?.code || "",
      sku: selectedProduct?.sku || "",
      category: (selectedProduct?.category || "").toLowerCase(),
      type: (selectedProduct?.type || "").toLowerCase(),
      purchasePrice: selectedProduct?.purchasePrice ?? "",
      salePrice: selectedProduct?.salePrice ?? "",
      discountPercentage: (selectedProduct as any)?.discountPercentage ?? "",
      vatRate: (selectedProduct as any)?.vatRate ?? "",
      brandId: (selectedProduct as any)?.brandId ?? "",
      supplierId: (selectedProduct as any)?.supplierId ?? "",
      description: selectedProduct?.description || "",
      productDetails: {
        model: selectedProduct?.productDetails?.model || "",
        color: selectedProduct?.productDetails?.color || "",
        material: (selectedProduct?.productDetails as any)?.material || "",
        size: selectedProduct?.productDetails?.size || "",
        shape:
          (selectedProduct?.productDetails as any)?.shape ||
          selectedProduct?.productDetails?.frameType ||
          (selectedProduct?.productDetails as any)?.frame_type ||
          "",
        modality: (selectedProduct?.productDetails as any)?.modality || "",
      },
    }),
    [selectedProduct],
  );

  const methods = useForm<any>({
    resolver: yupResolver(NewProductSchema),
    defaultValues,
    mode: 'all',
  });

  const {
    reset,
    handleSubmit,
    control,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const type = useWatch({
    control,
    name: "type",
  });

  const materialOptions = useMemo(
    () => MATERIAL_OPTIONS_BY_TYPE[type as ProductType] || [],
    [type]
  );

  useEffect(() => {
    if (type) {
      const currentCategory = methods.getValues("category");
      const validCategories =
        CATEGORY_BY_TYPE[type as ProductType]?.map((c) => c.value) || [];

      if (
        currentCategory &&
        !validCategories.includes(currentCategory as ProductCategory)
      ) {
        setValue("category", "");
      }
    }
  }, [type, setValue, methods]);

  useEffect(() => {
    const fetchBrandsSuppliers = async () => {
      try {
        const [brandRes, supplierRes] = await Promise.all([
          new BrandService().getAllBrands(),
          new SupplierService().getAllSuppliers(),
        ]);

        setBrandOptions(brandRes?.data || []);
        setSupplierOptions(supplierRes?.data || []);
      } catch {
        setBrandOptions([]);
        setSupplierOptions([]);
      }
    };

    fetchBrandsSuppliers();
  }, []);

  useEffect(() => {
    if (!isEdit || !selectedProduct) return;
    const selectedColor = selectedProduct?.productDetails?.color?.trim();
    if (!selectedColor) return;
    if (colorOptions.includes(selectedColor)) return;
    setColorOptions((prev) => Array.from(new Set([...prev, selectedColor])));
  }, [isEdit, selectedProduct, colorOptions]);

  useEffect(() => {
    if (isEdit && selectedProduct) {
      reset(defaultValues);
    }
    if (!isEdit) {
      reset(defaultValues);
    }
  }, [isEdit, selectedProduct, reset, defaultValues]);

  const onSubmit = async (data: FormValuesProps) => {
    try {
      const payload: any = {
        ...data,
      };

      // RHF select fields use `""` as the "not selected" value.
      // Convert those to `undefined` so backend DTO validation doesn't fail.
      payload.vatRate = payload.vatRate === "" ? undefined : payload.vatRate;
      payload.brandId = payload.brandId === "" ? undefined : payload.brandId;
      payload.supplierId =
        payload.supplierId === "" ? undefined : payload.supplierId;
      payload.discountPercentage =
        payload.discountPercentage === "" ? undefined : payload.discountPercentage;

      const selectedBrand = brandOptions.find(
        (b) => String(b.id) === String(payload.brandId)
      );
      const selectedSupplier = supplierOptions.find(
        (s) => String(s.id) === String(payload.supplierId)
      );

      payload.productDetails = {
        ...(payload.productDetails || {}),
        ...(selectedBrand?.name ? { brandName: selectedBrand.name } : {}),
        ...(selectedSupplier?.name ? { supplierName: selectedSupplier.name } : {}),
      };

      if (
        payload.productDetails &&
        Object.values(payload.productDetails).every((val: any) => !val)
      ) {
        delete payload.productDetails;
      }
      const productService = new ProductService();
      if (isEdit) {
        await productService.updateProduct(selectedProduct?.id!, payload);
      } else {
        await productService.createProduct(payload);
      }
      reset();
      if (!isEdit) {
        enqueueSnackbar("Create success!", {
          variant: "success",
        });
      } else {
        enqueueSnackbar("Update success!", {
          variant: "success",
        });
      }
      // navigate(PATH_DASHBOARD.product.list);
      onCancel(true);
    } catch (error: any) {
      enqueueSnackbar(
        Array.isArray(error?.message)
          ? error.message[0]
          : error?.message || "Something went wrong",
        {
          variant: "error",
        }
      );
      console.error(error);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3} sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: "flex" }}>
          <RHFTextField
            sx={{ mr: 1 }}
            name="name"
            disabled={isView}
            label="Product Name"
            required
          />

          <RHFTextField
            sx={{ mr: 1 }}
            name="code"
            disabled={isView}
            label="Product Code"
            helperText={!isView && "Auto generated if not provided"}
          />
        </Box>
        <Box sx={{ display: "flex" }}>
          <RHFTextField
            sx={{ mr: 1 }}
            name="sku"
            disabled={isView}
            label="Product SKU"
          />
        </Box>
        <Box sx={{ display: "flex" }}>
          <RHFSelect
            sx={{ mr: 1 }}
            name="type"
            label="Type"
            required
            disabled={isView}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: false, sx: { textTransform: "capitalize" } }}
          >
            {TYPE_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: 0.75,
                  typography: "body2",
                  textTransform: "capitalize",
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </RHFSelect>

          <RHFSelect
            sx={{ mr: 1 }}
            name="category"
            label="Category"
            required
            disabled={isView || !type}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: false, sx: { textTransform: "capitalize" } }}
          >
            {(CATEGORY_BY_TYPE[type as ProductType] || []).map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: 0.75,
                  typography: "body2",
                  textTransform: "capitalize",
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </RHFSelect>
        </Box>
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ color: "text.secondary", mb: 4 }}
          >
            Product Details
          </Typography>
          <Box sx={{ display: "flex", mb: 2, flexWrap: "wrap" }}>
            <RHFTextField
              sx={{ mr: 1, flex: 1, minWidth: 160 }}
              name="productDetails.model"
              disabled={isView}
              label="Model"
            />
            <RHFAutocomplete
              sx={{ mr: 1, flex: 1, minWidth: 160 }}
              name="productDetails.color"
              label="Color"
              disabled={isView}
              options={colorOptions}
              getOptionLabel={(option: string) => option}
              isOptionEqualToValue={(option, value) => option === value}
            />
            <RHFSelect
              sx={{ mr: 1, flex: 1, minWidth: 160 }}
              name="productDetails.material"
              label="Material"
              disabled={isView}
              InputLabelProps={{ shrink: true }}
            >
              {materialOptions.map((material) => (
                <MenuItem key={material} value={material}>
                  {material}
                </MenuItem>
              ))}
            </RHFSelect>
          </Box>
          <Box sx={{ display: "flex" }}>
            <RHFTextField
              sx={{ mr: 1 }}
              name="productDetails.size"
              disabled={isView}
              label="Size"
            />
            <RHFTextField
              sx={{ mr: 1 }}
              name="productDetails.shape"
              disabled={isView}
              label="Shape"
            />
            <RHFSelect
              sx={{ mr: 1 }}
              name="productDetails.modality"
              label="Modality"
              disabled={isView}
              InputLabelProps={{ shrink: true }}
            >
              {MODALITY_OPTIONS.map((modality) => (
                <MenuItem key={modality} value={modality}>
                  {modality}
                </MenuItem>
              ))}
            </RHFSelect>
          </Box>
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ color: "text.secondary", mb: 4 }}
          >
            Stock & Pricing
          </Typography>
          <Box sx={{ display: "flex" }}>
            {!isView && (
              <RHFTextField
                sx={{ mr: 1 }}
                name="purchasePrice"
                disabled={isView}
                type="number"
                label="Purchase Price"
                required
              />
            )}
            <RHFTextField
              sx={{ mr: 1 }}
              name="salePrice"
              disabled={isView}
              type="number"
              label="Sale Price"
              required
            />
            <RHFTextField
              sx={{ mr: 1 }}
              name="discountPercentage"
              disabled={isView}
              type="number"
              label="Discount (%)"
              inputProps={{ min: 0, max: 100 }}
            />
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", mt: 2 }}>
            <RHFSelect
              sx={{ mr: 1, flex: 1, minWidth: 160 }}
              name="vatRate"
              label="VAT Rate"
              disabled={isView}
              InputLabelProps={{ shrink: true }}
            >
              {VAT_RATE_OPTIONS.map((rate) => (
                <MenuItem key={rate} value={rate}>
                  {rate}%
                </MenuItem>
              ))}
            </RHFSelect>

            <RHFSelect
              sx={{ mr: 1, flex: 1, minWidth: 160 }}
              name="brandId"
              label="Brand"
              disabled={isView}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">Select Brand</MenuItem>
              {brandOptions.map((brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
            </RHFSelect>

            <RHFSelect
              sx={{ mr: 1, flex: 1, minWidth: 160 }}
              name="supplierId"
              label="Supplier"
              disabled={isView}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">Select Supplier</MenuItem>
              {supplierOptions.map((supplier) => (
                <MenuItem key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </MenuItem>
              ))}
            </RHFSelect>
          </Box>
        </Box>
      </Stack>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" color="inherit" onClick={() => onCancel()}>
          Cancel
        </Button>
        {!isView && (
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
          >
            {isEdit ? "Update" : "Add"}
          </LoadingButton>
        )}
      </DialogActions>
    </FormProvider>
  );
}
